import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  BookOpen, Sparkles, Feather, Star, ArrowRight,
  Sun, Loader2, CheckCircle2, X, Truck, RotateCcw, Lock, Tag, Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { DimensionDiagram } from "@/components/DimensionDiagram";
import { NewArrivals } from "@/components/NewArrivals";
import { PayPalCheckout } from "@/components/PayPalCheckout";
import { ContactWidget } from "@/components/ContactWidget";
import { Reveal, MaskLine } from "@/components/Motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAIN_PRODUCT_ID = "incognito-sun-reader";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/9136a7e2-ba00-41b8-98c8-00ad52a0d591/images/581f420e30bc89c10ccf69f0f3bfbee0e6dd4dea0e315ba646a0d29d526ec2f0.jpeg";
const LIFESTYLE_INDOOR = "https://images.unsplash.com/photo-1581915649317-41fc6eb9597d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxjbGVhciUyMGxlbnMlMjBnbGFzc2VzJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzgzMTQ5MTI3fDA&ixlib=rb-4.1.0&q=85";
const LIFESTYLE_OUTDOOR = "https://images.unsplash.com/photo-1763404215765-1c0365db0119?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxmYXNoaW9uJTIwbW9kZWwlMjB3ZWFyaW5nJTIwc3VuZ2xhc3NlcyUyMGRhcmslMjBzdHJlZXR8ZW58MHx8fHwxNzgzMTQ5MTI3fDA&ixlib=rb-4.1.0&q=85";

const REVIEWS = [
  { name: "Marcus T.", text: "Read my phone indoors, walk outside and they turn into shades. One pair does it all." },
  { name: "Elena R.", text: "The progressive zones are so smooth — phone, laptop, and across the room all in focus." },
  { name: "Devon K.", text: "Feather-light and the tint change is genuinely quick. No more juggling two pairs." },
  { name: "Priya S.", text: "Clear at my desk, dark on the patio. Exactly what I wanted from readers." },
  { name: "Jonah M.", text: "Great value at this price, and they look way more expensive than they are." },
  { name: "Ava L.", text: "Get compliments on the tortoise pair constantly. Comfortable all day." },
];

const FEATURES = [
  { icon: Sparkles, title: "Light-Adaptive Lenses", desc: "Photochromic lenses darken in sunlight and fade back to clear indoors — automatically." },
  { icon: BookOpen, title: "Progressive Multifocal", desc: "Seamless near-to-far reading zones let you read, work, and look up without switching glasses." },
  { icon: Feather, title: "Featherweight Frame", desc: "A lightweight, flexible frame built for comfortable all-day wear." },
];

const FAQS = [
  { q: "How do I choose my magnification strength?", a: "Pick the reading power you already use. If you're unsure, +1.50 to +2.00 suits most people starting out — a stronger number magnifies more for closer, smaller text. Just want the light-adaptive tint without magnification? Choose +0.00." },
  { q: "Do the lenses darken indoors?", a: "No — the tint is triggered by UV sunlight, so they stay clear indoors and darken when you step outside. The colder and brighter it is, the deeper the tint." },
  { q: "Are these prescription glasses?", a: "These are ready-to-wear progressive magnification readers (not custom prescription). The progressive design blends near, intermediate, and distance zones in one lens." },
  { q: "What's included?", a: "Your Incognito readers plus a protective case and a microfiber cleaning cloth." },
  { q: "How long does shipping take?", a: "Please allow 8–15 business days for order processing and shipping. You'll receive a confirmation email as soon as your order is on its way." },
  { q: "What's the return policy?", a: "30-day returns and a 1-year warranty against defects. If they're not right for you, send them back." },
];

const emptyPricing = { subtotal: 0, savings: 0, shipping: 0, total: 0, free_shipping: false };

const WaitlistSection = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, { email, interest: "prescription" });
      setDone(true);
      setEmail("");
      toast.success("You're on the list! We'll be in touch.");
    } catch (err) {
      const msg = err?.response?.status === 422 ? "Please enter a valid email address." : "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-t border-white/10" data-testid="waitlist">
      <div className="rounded-2xl bg-[#121212] border border-white/10 p-10 md:p-16 text-center max-w-3xl mx-auto">
        <Mail className="h-8 w-8 mx-auto text-[#8BA3B3]" strokeWidth={1.5} />
        <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-medium mt-6">Prescription lenses are coming.</h2>
        <p className="mt-4 text-white/60 leading-relaxed">Join the list to be first to know when prescription and restock drops go live. No spam — just the good stuff.</p>
        {done ? (
          <div className="mt-8 flex items-center justify-center gap-2 text-emerald-400" data-testid="waitlist-success">
            <CheckCircle2 className="h-5 w-5" /> You're on the list.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com" data-testid="waitlist-input"
              className="h-12 rounded-full bg-black border-white/20 text-white placeholder:text-white/40 px-5 focus-visible:ring-white/30" />
            <Button type="submit" disabled={submitting} data-testid="waitlist-submit"
              className="h-12 rounded-full bg-white text-black hover:bg-white/85 px-7 font-semibold shrink-0">
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Notify Me"}
            </Button>
          </form>
        )}
      </div>
    </section>
  );
};

export default function Landing() {
  const [product, setProduct] = useState(null);
  const [color, setColor] = useState("Black");
  const [strength, setStrength] = useState("+2.00");
  const [quantity, setQuantity] = useState(1);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [appliedCode, setAppliedCode] = useState("");
  const [featured, setFeatured] = useState(null);
  const [pricing, setPricing] = useState(emptyPricing);
  const [loading, setLoading] = useState(false);
  const [statusState, setStatusState] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const heroFade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    axios.get(`${API}/product/${MAIN_PRODUCT_ID}`).then((r) => {
      setProduct(r.data);
      setColor(r.data.colors[0].name);
      setStrength(r.data.strengths.includes("+2.00") ? "+2.00" : r.data.strengths[0]);
    }).catch(() => {});
    axios.get(`${API}/config`).then((r) => {
      if (r.data.featured_discount) {
        setFeatured(r.data.featured_discount);
        setAppliedCode(r.data.featured_code);
        setCode(r.data.featured_code);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    axios.post(`${API}/quote`, { product_id: MAIN_PRODUCT_ID, quantity, code: appliedCode || null, email: email || null })
      .then((r) => setPricing(r.data)).catch(() => {});
  }, [quantity, appliedCode, email]);

  const applyCode = async () => {
    const c = code.trim();
    if (!c) return;
    try {
      const r = await axios.post(`${API}/quote`, { product_id: MAIN_PRODUCT_ID, quantity, code: c, email: email || null });
      setPricing(r.data);
      if (r.data.code_valid) { setAppliedCode(c); toast.success(`Code ${r.data.discount_code} applied!`); }
      else { setAppliedCode(""); toast.error(r.data.code_error || "Invalid or expired code."); }
    } catch (e) { toast.error("Could not apply code."); }
  };

  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= 6) { setStatusState("failed"); return; }
    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (res.data.payment_status === "paid") { setStatusState("success"); return; }
      if (res.data.status === "expired") { setStatusState("failed"); return; }
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    } catch (e) {
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const cancelled = params.get("checkout");
    if (sessionId) {
      setStatusState("checking");
      pollStatus(sessionId);
      window.history.replaceState({}, "", window.location.pathname);
    } else if (cancelled === "cancelled") {
      toast.error("Checkout cancelled. Your cart is safe.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [pollStatus]);

  const handleCheckout = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email for your receipt.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/checkout/session`, {
        product_id: MAIN_PRODUCT_ID, color, strength, quantity, email, phone: phone || null, code: appliedCode || null,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  const scrollToBuy = () => scrollTo("buy");
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (window.lenis) window.lenis.scrollTo(el, { offset: -72 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  if (!product) {
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-white/50" /></div>;
  }

  const activeColor = product.colors.find((c) => c.name === color) || product.colors[0];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden" data-testid="landing-page">
      <div className="grain-overlay" aria-hidden="true" />
      {/* NAV */}
      <nav className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/50 border-b border-white/10">
        <div className="px-6 md:px-12 lg:px-24 h-16 flex items-center justify-between">
          <span className="font-heading text-xl tracking-[0.35em] font-medium" data-testid="brand-logo">INCOGNITO</span>
          <Button onClick={scrollToBuy} data-testid="nav-buy-button"
            className="rounded-full bg-white text-black hover:bg-white/85 active:scale-[0.96] transition-transform h-9 px-5 text-sm font-semibold">
            Buy Now
          </Button>
        </div>
      </nav>

      {/* PROMO BAR */}
      <div className="fixed top-16 inset-x-0 z-40 bg-white text-black text-center text-xs md:text-sm py-2 font-medium tracking-wide" data-testid="promo-bar">
        {featured
          ? `🎉 Code ${featured.code} auto-applied — ${featured.type === "bogo" ? "buy 2, 2nd pair 50% off" : featured.type === "percent" ? `${featured.value}% off` : `$${featured.value} off`} at checkout · Free shipping $49+`
          : "Buy 2 — 2nd pair 50% off · Free shipping on orders $49+"}
      </div>

      {/* HERO */}
      <header ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0 will-change-transform">
          <img src={HERO_IMG} alt="Person wearing Incognito photochromic readers" className="w-full h-full object-cover [transform:scaleX(-1)]" style={{ objectPosition: "center" }} />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent" />
        <motion.div style={{ opacity: heroFade }} className="relative px-6 md:px-12 lg:px-24 pb-24 pt-32 w-full">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.15 }}
            className="text-[#8BA3B3] uppercase tracking-[0.3em] text-xs md:text-sm mb-6 flex items-center gap-3">
            <span className="h-px w-10 bg-[#8BA3B3]/60" /> Photochromic Progressive Readers
          </motion.p>
          <h1 className="font-heading font-semibold tracking-tighter text-5xl sm:text-7xl lg:text-[7.5rem] leading-[0.88] max-w-5xl">
            <MaskLine delay={0.2}>READ ANYWHERE.</MaskLine>
            <MaskLine delay={0.35}>RAIN OR SHINE.</MaskLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-8 text-base md:text-lg text-white/70 max-w-xl leading-relaxed">
            Progressive multifocal readers with light-adaptive lenses that darken in the sun and clear up indoors — one featherweight frame for every moment.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-10 flex flex-wrap items-center gap-4">
            <Button onClick={scrollToBuy} data-testid="hero-cta"
              className="group rounded-full bg-white text-black hover:bg-white/85 active:scale-[0.97] transition-transform h-14 px-8 text-base font-semibold">
              Shop Now — ${product.price}
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
            <span className="text-sm text-white/50 flex items-center gap-2">
              <Star className="h-4 w-4 fill-white text-white" /> 4.9 · 3,200+ reviews
            </span>
          </motion.div>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.1 }}
          className="absolute bottom-8 right-6 md:right-12 lg:right-24 hidden sm:flex items-center gap-3 text-white/40 text-xs uppercase tracking-[0.25em]">
          Scroll
          <span className="relative h-10 w-px bg-white/20 overflow-hidden">
            <motion.span className="absolute inset-x-0 top-0 h-1/2 bg-white/80"
              animate={{ y: ["-100%", "200%"] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }} />
          </span>
        </motion.div>
      </header>

      {/* MANIFESTO — numbered chapters */}
      <section className="px-6 md:px-12 lg:px-24 py-28 md:py-40" data-testid="how-it-works">
        <Reveal className="max-w-3xl">
          <p className="text-[#8BA3B3] uppercase tracking-[0.3em] text-xs mb-5 flex items-center gap-3">
            <span className="h-px w-10 bg-[#8BA3B3]/60" /> The Incognito method
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.02]">
            One frame. Every light. Zero compromise.
          </h2>
          <p className="mt-6 text-white/60 text-base md:text-lg leading-relaxed">
            Three quiet feats of engineering, working together so you never reach for a second pair again.
          </p>
        </Reveal>

        {/* Chapter 01 */}
        <div className="mt-24 md:mt-32 grid md:grid-cols-12 gap-8 md:gap-16 items-center">
          <Reveal className="md:col-span-7 order-2 md:order-1">
            <span className="font-heading text-[5rem] md:text-[8rem] leading-none text-white/10 block -mb-4 md:-mb-8 select-none">01</span>
            <div className="flex items-center gap-2 text-[#8BA3B3] mb-4"><Sun className="h-5 w-5" /><span className="text-xs uppercase tracking-[0.25em]">Light-adaptive</span></div>
            <h3 className="font-heading text-3xl md:text-4xl tracking-tight font-medium">Clear indoors. Shaded outdoors.</h3>
            <p className="mt-5 text-white/60 leading-relaxed max-w-md">
              Photochromic molecules react to UV sunlight — darkening the moment you step outside and fading back to crystal clarity the instant you head back in. Colder, brighter days deepen the tint.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-5 order-1 md:order-2">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/5] border border-white/10 group">
              <img src={LIFESTYLE_OUTDOOR} alt="Dark lens state outdoors" className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            </div>
          </Reveal>
        </div>

        {/* Chapter 02 */}
        <div className="mt-20 md:mt-28 grid md:grid-cols-12 gap-8 md:gap-16 items-center">
          <Reveal className="md:col-span-5">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/5] border border-white/10 group">
              <img src={LIFESTYLE_INDOOR} alt="Clear lens state indoors" className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-7 md:text-right md:flex md:flex-col md:items-end">
            <span className="font-heading text-[5rem] md:text-[8rem] leading-none text-white/10 block -mb-4 md:-mb-8 select-none">02</span>
            <div className="flex items-center gap-2 text-[#8BA3B3] mb-4 md:flex-row-reverse"><BookOpen className="h-5 w-5" /><span className="text-xs uppercase tracking-[0.25em]">Progressive multifocal</span></div>
            <h3 className="font-heading text-3xl md:text-4xl tracking-tight font-medium">Read, work, and look up — no swapping.</h3>
            <p className="mt-5 text-white/60 leading-relaxed max-w-md">
              Seamless near-to-far zones blend into a single lens. Your phone, your laptop, and the world across the room all stay in focus as your eyes travel down the lens.
            </p>
          </Reveal>
        </div>
      </section>

      {/* EDITORIAL MARQUEE */}
      <section className="border-y border-white/10 py-6 md:py-8 overflow-hidden editorial-marquee" data-testid="editorial-marquee" aria-hidden="true">
        <div className="marquee-mask overflow-hidden">
          <div className="flex w-max animate-marquee-slow items-center">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex items-center gap-8 pr-8">
                <span className="font-heading text-4xl md:text-6xl tracking-tighter text-white/90">Read anywhere</span>
                <span className="text-[#8BA3B3] text-4xl md:text-6xl">✦</span>
                <span className="font-heading text-4xl md:text-6xl tracking-tighter text-white/25">Rain or shine</span>
                <span className="text-[#8BA3B3] text-4xl md:text-6xl">✦</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES BENTO — Chapter 03 */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28" data-testid="features">
        <Reveal className="mb-12">
          <span className="font-heading text-[5rem] md:text-[7rem] leading-none text-white/10 block -mb-4 md:-mb-6 select-none">03</span>
          <div className="flex items-center gap-2 text-[#8BA3B3] mb-4"><Feather className="h-5 w-5" /><span className="text-xs uppercase tracking-[0.25em]">Built to disappear</span></div>
          <h3 className="font-heading text-3xl md:text-4xl tracking-tight font-medium max-w-xl">Featherweight comfort you forget you're wearing.</h3>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="rounded-2xl bg-[#121212] border border-white/10 p-7 hover:border-[#8BA3B3]/40 hover:bg-[#161616] transition-colors duration-500">
              <f.icon className="h-7 w-7 text-[#8BA3B3]" strokeWidth={1.5} />
              <h3 className="font-heading text-xl mt-6">{f.title}</h3>
              <p className="text-white/55 text-sm mt-3 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PRODUCT SELECTOR + CHECKOUT */}
      <section id="buy" className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-t border-white/10" data-testid="product-section">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div className="lg:sticky lg:top-28">
            <div className="rounded-2xl overflow-hidden bg-white aspect-square flex items-center justify-center">
              <img src={activeColor.image} alt={`${product.name} — ${activeColor.name}`} className="w-full h-full object-contain p-6" data-testid="product-image" />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-4">
              {product.colors.map((c) => (
                <button key={c.name} onClick={() => setColor(c.name)} data-testid={`thumb-${c.name.toLowerCase()}`}
                  className={`rounded-xl overflow-hidden bg-white border-2 transition-colors ${color === c.name ? "border-white" : "border-transparent hover:border-white/40"}`}>
                  <img src={c.image} alt={c.name} className="w-full h-full object-contain p-2 aspect-square" />
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-4">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-white text-white" />)}
              <span className="text-sm text-white/50 ml-1">4.9 (3,214)</span>
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium">{product.name}</h2>
            <div className="flex items-baseline gap-4 mt-5">
              <span className="text-3xl font-semibold" data-testid="product-price">${product.price}</span>
              <span className="text-lg text-white/40 line-through">${product.compare_at_price}</span>
              <span className="text-xs uppercase tracking-widest text-[#8BA3B3] border border-[#8BA3B3]/40 rounded-full px-3 py-1">Launch price</span>
            </div>
            <p className="mt-6 text-white/60 leading-relaxed">{product.description}</p>

            {/* Color */}
            <div className="mt-8">
              <p className="text-sm uppercase tracking-widest text-white/50 mb-3">Frame color — <span className="text-white">{color}</span></p>
              <div className="flex flex-wrap gap-3" data-testid="color-group">
                {product.colors.map((c) => (
                  <button key={c.name} onClick={() => setColor(c.name)} data-testid={`color-${c.name.toLowerCase()}`}
                    className={`rounded-full border px-5 py-2.5 text-sm transition-colors ${color === c.name ? "bg-white text-black border-white" : "border-white/20 text-white/70 hover:border-white/50"}`}>
                    {c.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Strength */}
            <div className="mt-6">
              <p className="text-sm uppercase tracking-widest text-white/50 mb-3">Magnification — <span className="text-white">{strength}</span></p>
              <div className="flex flex-wrap gap-3" data-testid="strength-group">
                {product.strengths.map((s) => (
                  <button key={s} onClick={() => setStrength(s)} data-testid={`strength-${s.replace(/[+.]/g, "")}`}
                    className={`rounded-full border px-5 py-2.5 text-sm transition-colors ${strength === s ? "bg-white text-black border-white" : "border-white/20 text-white/70 hover:border-white/50"}`}>
                    {s}
                  </button>
                ))}
              </div>
              <p className="text-xs text-[#8BA3B3] mt-3" data-testid="strength-hint">
                {strength === "+0.00"
                  ? "No magnification — light-adaptive tint only, for those who don't need readers."
                  : "Choose the reading power you already use. Unsure? +1.50 to +2.00 suits most first-time readers."}
              </p>
            </div>

            {/* Quantity */}
            <div className="mt-6">
              <p className="text-sm uppercase tracking-widest text-white/50 mb-3">Quantity</p>
              <div className="inline-flex items-center rounded-full border border-white/20">
                <button onClick={() => setQuantity((q) => Math.max(1, q - 1))} data-testid="qty-decrease"
                  className="h-11 w-11 text-xl hover:bg-white/10 rounded-l-full transition-colors">−</button>
                <span className="w-12 text-center text-lg" data-testid="qty-value">{quantity}</span>
                <button onClick={() => setQuantity((q) => Math.min(10, q + 1))} data-testid="qty-increase"
                  className="h-11 w-11 text-xl hover:bg-white/10 rounded-r-full transition-colors">+</button>
              </div>
            </div>

            {/* Promo code */}
            <div className="mt-6">
              <p className="text-sm uppercase tracking-widest text-white/50 mb-3">Promo code</p>
              <div className="flex gap-3 max-w-md">
                <Input value={code} onChange={(e) => setCode(e.target.value)}
                  placeholder="Enter code" data-testid="promo-input"
                  className="h-12 rounded-full bg-black border-white/20 text-white placeholder:text-white/40 px-5 focus-visible:ring-white/30 uppercase" />
                <Button onClick={applyCode} data-testid="promo-apply" variant="outline"
                  className="h-12 rounded-full border-white/25 bg-transparent text-white hover:bg-white/10 px-6 shrink-0">
                  Apply
                </Button>
              </div>
            </div>

            {/* Price breakdown */}
            <div className="mt-8 rounded-2xl bg-[#121212] border border-white/10 p-6 space-y-3" data-testid="price-breakdown">
              <div className="flex justify-between text-white/70"><span>Subtotal</span><span data-testid="subtotal">${pricing.subtotal.toFixed(2)}</span></div>
              {pricing.savings > 0 && (
                <div className="flex justify-between text-emerald-400"><span className="flex items-center gap-2"><Tag className="h-4 w-4" /> BOGO savings</span><span data-testid="savings">−${pricing.savings.toFixed(2)}</span></div>
              )}
              {pricing.discount_amount > 0 && (
                <div className="flex justify-between text-emerald-400"><span className="flex items-center gap-2"><Tag className="h-4 w-4" /> Code {pricing.discount_code}</span><span data-testid="discount">−${pricing.discount_amount.toFixed(2)}</span></div>
              )}
              <div className="flex justify-between text-white/70"><span>Shipping</span><span data-testid="shipping">{pricing.free_shipping ? "FREE" : `$${pricing.shipping.toFixed(2)}`}</span></div>
              {!pricing.free_shipping && (
                <p className="text-xs text-[#8BA3B3]">Add ${(49 - (pricing.subtotal - pricing.discount_amount)).toFixed(2)} more for free shipping.</p>
              )}
              <div className="border-t border-white/10 pt-3 flex justify-between text-lg font-semibold"><span>Total</span><span data-testid="total">${pricing.total.toFixed(2)}</span></div>
            </div>

            <div className="mt-6">
              <p className="text-sm uppercase tracking-widest text-white/50 mb-3">Email for receipt</p>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com" data-testid="checkout-email"
                className="h-12 rounded-full bg-black border-white/20 text-white placeholder:text-white/40 px-5 focus-visible:ring-white/30 max-w-md" />
            </div>

            <div className="mt-4">
              <p className="text-sm uppercase tracking-widest text-white/50 mb-3">Phone <span className="text-white/30 normal-case tracking-normal">(optional)</span></p>
              <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 (555) 000-0000" data-testid="checkout-phone"
                className="h-12 rounded-full bg-black border-white/20 text-white placeholder:text-white/40 px-5 focus-visible:ring-white/30 max-w-md" />
            </div>

            <Button onClick={handleCheckout} disabled={loading} data-testid="checkout-button"
              className="mt-6 w-full rounded-full bg-white text-black hover:bg-white/85 active:scale-[0.99] transition-transform h-14 px-10 text-base font-semibold">
              {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Redirecting…</> : <>Buy with Card — ${pricing.total.toFixed(2)}</>}
            </Button>

            <div className="flex items-center gap-4 my-4">
              <span className="h-px flex-1 bg-white/10" />
              <span className="text-xs uppercase tracking-widest text-white/40">or</span>
              <span className="h-px flex-1 bg-white/10" />
            </div>

            <PayPalCheckout productId={MAIN_PRODUCT_ID} color={color} strength={strength}
              quantity={quantity} email={email} phone={phone} code={appliedCode} onSuccess={() => setStatusState("success")} />

            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm text-white/50">
              <span className="flex items-center gap-2"><Truck className="h-4 w-4" /> Free shipping $49+</span>
              <span className="flex items-center gap-2"><RotateCcw className="h-4 w-4" /> 30-day returns</span>
              <span className="flex items-center gap-2"><Lock className="h-4 w-4" /> Secure checkout</span>
            </div>
            <p className="mt-4 text-xs text-white/40" data-testid="shipping-note">
              Allow 8–15 business days for order processing and shipping.
            </p>
          </div>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <NewArrivals onPaid={() => setStatusState("success")} featuredCode={featured?.code} />

      {/* REVIEWS MARQUEE */}
      <section className="py-20 md:py-28 border-t border-white/10" data-testid="reviews">
        <Reveal>
          <p className="text-center text-[#8BA3B3] uppercase tracking-[0.3em] text-xs mb-12">What people are saying</p>
        </Reveal>
        <div className="marquee-mask overflow-hidden">
          <div className="flex w-max animate-marquee gap-5">
            {[...REVIEWS, ...REVIEWS].map((r, i) => (
              <div key={`${r.name}-${i}`} className="w-[320px] shrink-0 rounded-2xl bg-[#121212] border border-white/10 p-7">
                <div className="flex gap-1 mb-4">{[...Array(5)].map((_, j) => <Star key={j} className="h-3.5 w-3.5 fill-white text-white" />)}</div>
                <p className="text-white/80 leading-relaxed">"{r.text}"</p>
                <p className="mt-5 text-sm text-white/40">{r.name} · Verified Buyer</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ + SIZE GUIDE */}
      <section id="faq" className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-t border-white/10" data-testid="faq">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium">Questions,<br />answered.</h2>
            <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-b border-white/15">
                  <AccordionTrigger className="text-left text-lg md:text-xl font-normal hover:no-underline py-6" data-testid={`faq-trigger-${i}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-white/60 leading-relaxed text-base pb-6">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          <div>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium">Size guide</h2>
            <p className="mt-6 text-white/60 leading-relaxed">A medium-fit unisex frame designed to suit most face shapes.</p>
            <div className="mt-10 rounded-2xl bg-[#121212] border border-white/10 divide-y divide-white/10" data-testid="size-guide">
              {[
                ["Lens width", "56 mm / 2.2 in"],
                ["Lens height", "48 mm / 1.89 in"],
                ["Frame width", "141 mm / 5.55 in"],
                ["Temple length", "140 mm / 5.5 in"],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between px-6 py-4">
                  <span className="text-white/60">{k}</span>
                  <span className="font-medium">{v}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-2xl bg-[#121212] border border-white/10 p-6">
              <DimensionDiagram />
            </div>
          </div>
        </div>
      </section>

      {/* WAITLIST */}
      <WaitlistSection />

      {/* FOOTER */}
      <footer className="px-6 md:px-12 lg:px-24 pt-24 pb-16 border-t border-white/10" data-testid="footer">
        <div className="flex flex-col lg:flex-row justify-between gap-12">
          <div>
            <p className="text-white/60 max-w-sm leading-relaxed">Light-adaptive progressive readers. Read anywhere, rain or shine.</p>
            <Button onClick={scrollToBuy} data-testid="footer-cta"
              className="mt-8 rounded-full bg-white text-black hover:bg-white/85 h-12 px-8 font-semibold">
              Shop Now <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
          <div className="flex gap-16 text-sm text-white/50">
            <div className="space-y-3">
              <p className="text-white/80 uppercase tracking-widest text-xs mb-4">Shop</p>
              <button onClick={() => scrollTo("buy")} data-testid="footer-link-readers" className="block text-left hover:text-white transition-colors">Readers</button>
              <button onClick={() => scrollTo("new-arrivals")} data-testid="footer-link-accessories" className="block text-left hover:text-white transition-colors">Accessories</button>
            </div>
            <div className="space-y-3">
              <p className="text-white/80 uppercase tracking-widest text-xs mb-4">Support</p>
              <button onClick={() => scrollTo("faq")} data-testid="footer-link-shipping" className="block text-left hover:text-white transition-colors">Shipping</button>
              <button onClick={() => scrollTo("faq")} data-testid="footer-link-returns" className="block text-left hover:text-white transition-colors">Returns</button>
              <button onClick={() => scrollTo("faq")} data-testid="footer-link-warranty" className="block text-left hover:text-white transition-colors">Warranty</button>
              <button onClick={() => setContactOpen(true)} data-testid="contact-email"
                className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors">
                <Mail className="h-4 w-4" /> Contact us
              </button>
            </div>
          </div>
        </div>
        <h2 className="font-heading text-[16vw] leading-none tracking-tighter font-semibold mt-16 text-white/95 select-none">INCOGNITO</h2>
        <p className="text-white/30 text-sm mt-8">© {new Date().getFullYear()} Incognito Eyewear. All rights reserved.</p>
      </footer>

      {/* CHECKOUT STATUS MODAL */}
      <ContactWidget open={contactOpen} setOpen={setContactOpen} />
      <Dialog open={!!statusState} onOpenChange={(o) => !o && setStatusState(null)}>
        <DialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-md" data-testid="status-modal">
          {statusState === "checking" && (
            <div className="py-8 text-center">
              <Loader2 className="h-10 w-10 animate-spin mx-auto text-[#8BA3B3]" />
              <h3 className="font-heading text-2xl mt-6">Confirming your order…</h3>
              <p className="text-white/60 mt-2">Hang tight while we verify your payment.</p>
            </div>
          )}
          {statusState === "success" && (
            <div className="py-8 text-center">
              <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-400" />
              <h3 className="font-heading text-3xl mt-6">Order confirmed!</h3>
              <p className="text-white/60 mt-3">Thank you for choosing Incognito. A confirmation email is on its way, and your readers will ship shortly.</p>
              <Button onClick={() => setStatusState(null)} data-testid="status-close"
                className="mt-8 rounded-full bg-white text-black hover:bg-white/85 h-12 px-8 font-semibold">Continue</Button>
            </div>
          )}
          {statusState === "failed" && (
            <div className="py-8 text-center">
              <X className="h-12 w-12 mx-auto text-red-400" />
              <h3 className="font-heading text-2xl mt-6">Payment not completed</h3>
              <p className="text-white/60 mt-3">We couldn't confirm your payment. If you were charged, contact support and we'll sort it out.</p>
              <Button onClick={() => setStatusState(null)} data-testid="status-close"
                className="mt-8 rounded-full bg-white text-black hover:bg-white/85 h-12 px-8 font-semibold">Close</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
